import type { Nullable } from '@ps/utils';
import type {
  DefaultSupplierAllocation,
  FeasibilitySupplierBreakdown,
  RateCards,
  SupplierAllocation,
  SupplierAllocationsUpdateStatus,
  SupplierBlend,
  SupplierGroupTemplate,
  Survey,
} from '@ps/survey-utils';
import type { SuppliersMetaData } from '@ps/survey-creation-data-access';

/**
 * Everything the supplier-management drawer needs for a launched survey,
 * rebuilt from a `surveyId` alone. The creation wizard seeds the same data from
 * its live form; this slice is the standalone source for the manage-survey tab,
 * where no creation slice exists.
 */
export interface ManageSupplierState {
  readonly survey: Nullable<Survey>;
  readonly allocations: readonly SupplierAllocation[];
  readonly feasibilitySuppliers: readonly FeasibilitySupplierBreakdown[];
  readonly blends: readonly SupplierBlend[];
  readonly groupTemplates: readonly SupplierGroupTemplate[];
  readonly defaultAllocations: readonly DefaultSupplierAllocation[];
  readonly rateCards: Nullable<RateCards>;
  readonly suppliersMetaData: SuppliersMetaData;
  // `requested` flips on the first load so `ready` cannot report true from the
  // all-idle initial state before any fetch has started.
  readonly requested: boolean;
  readonly loading: boolean;
  readonly loadError: Nullable<string>;
  readonly allocationsUpdateStatus: SupplierAllocationsUpdateStatus;
  readonly blendSaving: boolean;
  readonly savedBlendId: Nullable<string>;
}

/** Null Object: no pricing yet — the panel opens on supplierRates. */
export const EMPTY_SUPPLIERS_META_DATA: SuppliersMetaData = {
  blendId: '',
  priceOverride: null,
  defaultPurePriceOverride: false,
  isPurePriceRateCardEnable: false,
  supplierDefaultAllocation: false,
  surveyPauseThreshold: null,
  manualOverride: false,
};
